/**
 * Points Overlay Component for WebGLChart
 * Renders data points as SVG circles when enabled
 * Uses EXACTLY the same coordinate calculations as WebGL for alignment
 * Shows points only when zoomed in enough (≤10000 visible points)
 */

import React, { useMemo, useEffect } from 'react';
import { ZoomState } from '../types';
import { CHART_PADDING } from '../constants';

interface PointsOverlayProps {
  timestamps: number[];
  values: (number | null)[];
  xMin: number;
  xMax: number;
  yMin: number;
  yMax: number;
  zoomState: ZoomState;
  chartWidth: number;
  chartHeight: number;
  enabled: boolean;
  onVisibilityChange?: (visible: boolean) => void;
}

// Maximum number of visible points to show data points (above this, too many to render)
const MAX_VISIBLE_POINTS_FOR_DISPLAY = 10000;

// Binary search to find first index where arr[index] >= target
const findFirstIndex = (arr: number[], target: number): number => {
  let left = 0;
  let right = arr.length;
  while (left < right) {
    const mid = Math.floor((left + right) / 2);
    if (arr[mid] < target) {
      left = mid + 1;
    } else {
      right = mid;
    }
  }
  return left;
};

export const PointsOverlay: React.FC<PointsOverlayProps> = ({
  timestamps,
  values,
  xMin,
  xMax,
  yMin,
  yMax,
  zoomState,
  chartWidth,
  chartHeight,
  enabled,
  onVisibilityChange,
}) => {
  // Calculate visible points using EXACTLY the same formulas as WebGL
  const visiblePoints = useMemo(() => {
    if (!enabled || chartWidth <= 0 || chartHeight <= 0) return [];
    if (timestamps.length === 0 || values.length === 0) return [];

    // Guard against invalid ranges
    if (!Number.isFinite(xMin) || !Number.isFinite(xMax) || xMin >= xMax) {
      return [];
    }
    if (!Number.isFinite(yMin) || !Number.isFinite(yMax) || yMin >= yMax) {
      return [];
    }

    // X range calculation - SAME as WebGL (useWebGLInstance.ts line 77)
    const xRange = xMax - xMin || 1;

    // Y range calculation - SAME as WebGL (useWebGLInstance.ts lines 78-82)
    const yRangeRaw = yMax - yMin || 1;
    const yPadding = yRangeRaw * 0.05;
    const yMinPadded = yMin - yPadding;
    const yMaxPadded = yMax + yPadding;
    const yRangePadded = yMaxPadded - yMinPadded;

    // Calculate visible time range based on zoom
    // Reverse the WebGL coordinate transformation to find visible data range
    // Screen edge 0 maps to zoomedX = -1, screen edge chartWidth maps to zoomedX = 1
    // zoomedX = normalizedX * scaleX + offsetX
    // normalizedX = (zoomedX - offsetX) / scaleX
    // normalizedX = ((t - xMin) / xRange) * 2 - 1
    // t = (normalizedX + 1) / 2 * xRange + xMin

    const leftEdgeZoomed = -1;  // Left edge of screen in zoomed coordinates
    const rightEdgeZoomed = 1;  // Right edge of screen in zoomed coordinates

    const leftEdgeNorm = (leftEdgeZoomed - zoomState.offsetX) / zoomState.scaleX;
    const rightEdgeNorm = (rightEdgeZoomed - zoomState.offsetX) / zoomState.scaleX;

    const visibleTimeStart = ((leftEdgeNorm + 1) / 2) * xRange + xMin;
    const visibleTimeEnd = ((rightEdgeNorm + 1) / 2) * xRange + xMin;

    // Use binary search to find indices of visible data range
    // Add small buffer to avoid edge clipping
    const buffer = xRange * 0.01;
    const startIdx = Math.max(0, findFirstIndex(timestamps, visibleTimeStart - buffer) - 1);
    const endIdx = Math.min(timestamps.length - 1, findFirstIndex(timestamps, visibleTimeEnd + buffer));

    // Calculate visible point count - only show points when not too many
    const visibleCount = endIdx - startIdx + 1;

    // If too many points visible, don't render them (would be too dense)
    if (visibleCount > MAX_VISIBLE_POINTS_FOR_DISPLAY) {
      return [];
    }

    const points: { x: number; y: number }[] = [];

    // Show all visible data points
    for (let i = startIdx; i <= endIdx; i++) {
      const t = timestamps[i];
      const v = values[i];

      // Skip null values - SAME as WebGL (useWebGLInstance.ts line 91)
      if (v === null || !Number.isFinite(v)) continue;

      // X normalization - EXACTLY as WebGL (useWebGLInstance.ts line 86)
      const normalizedX = ((t - xMin) / xRange) * 2 - 1;

      // Apply zoom transformation - SAME as WebGL (useWebGLInstance.ts lines 103-104)
      const zoomedX = normalizedX * zoomState.scaleX + zoomState.offsetX;

      // Convert from [-1, 1] to screen coordinates [0, chartWidth]
      const screenX = ((zoomedX + 1) / 2) * chartWidth;

      // Y normalization - EXACTLY as WebGL (useWebGLInstance.ts line 92)
      const normalizedY = ((v - yMinPadded) / yRangePadded) * 2 - 1;

      // Apply Y zoom (WebGL uses gScaleY and gOffsetY)
      const zoomedY = normalizedY * zoomState.scaleY + zoomState.offsetY;

      // Convert from [-1, 1] to screen coordinates [0, chartHeight]
      const screenY = ((1 - zoomedY) / 2) * chartHeight;

      // Only include points that are visible on screen
      if (screenX >= -5 && screenX <= chartWidth + 5 &&
          screenY >= -5 && screenY <= chartHeight + 5) {
        points.push({ x: screenX, y: screenY });
      }
    }

    return points;
  }, [enabled, timestamps, values, xMin, xMax, yMin, yMax, zoomState, chartWidth, chartHeight]);

  // Notify parent about visibility status
  const isVisible = enabled && visiblePoints.length > 0;
  useEffect(() => {
    onVisibilityChange?.(isVisible);
  }, [isVisible, onVisibilityChange]);

  if (!enabled || visiblePoints.length === 0) return null;

  return (
    <svg
      className="absolute pointer-events-none"
      style={{
        left: CHART_PADDING.left,
        top: CHART_PADDING.top,
        width: chartWidth,
        height: chartHeight,
      }}
      viewBox={`0 0 ${chartWidth} ${chartHeight}`}
      preserveAspectRatio="none"
    >
      {visiblePoints.map((point, i) => (
        <circle
          key={i}
          cx={point.x}
          cy={point.y}
          r={3}
          fill="#14B8A6"
          stroke="#0D9488"
          strokeWidth={1}
        />
      ))}
    </svg>
  );
};
